import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import {
  DocLinkIcon,
  EditPenIcon,
  MoreIcon,
  PinFilledIcon,
  PinOutlinedIcon,
  ReplyIcon,
} from '@sharedCR/coreDesignSystem/icons';
import TextFieldCDS from 'app/shared/componentsRestyled/coreDesignSystem/form/TextFieldCDS';
import MenuPopoverCDS from 'app/shared/componentsRestyled/coreDesignSystem/MenuPopoverCDS';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { SHORT_DATETIME_FORMAT, SHORT_TIME_FORMAT } from 'constants/date';
import { copyToClipboard } from 'utils/copyToClipboard';

import {
  addReactionAction,
  removeReactionAction,
  updateMessageAction,
} from 'store/teamChat/actions';

import {
  buildTeamChatMessagePermalink,
  getMemberId,
  getMessageFile,
  getMessageId,
  getMessageReactions,
  getMessageSender,
  getMessageSubjectRef,
  getMessageText,
  getMessageTimestamp,
  getMessageUpdated,
  getTeamChatMessageDomId,
  isFileMessage,
  renderMessageTextTokens,
  resolveCurrentChatUserId,
} from '../TeamChat.utils';
import TeamChatEntityChip from '../TeamChatEntityChip';
import TeamChatFieldAnchor from '../TeamChatFieldAnchor';
import TeamChatFileMessage from '../TeamChatFileMessage';
import TeamChatMemberAvatar from '../TeamChatMemberAvatar';
import TeamChatReactionPicker from '../TeamChatReactionPicker';
import TeamChatReactions from '../TeamChatReactions';
import TeamChatReplyPreview from '../TeamChatReplyPreview';

import {
  EMPTY_MEMBERS,
  LINK_COPIED_DURATION_MS,
  MESSAGE_ROW_CLASS_NAME,
  UNKNOWN_SENDER_LABEL,
} from './TeamChatMessage.constants';
import {
  actionButtonStyles,
  actionsStyles,
  avatarColumnStyles,
  editActionsStyles,
  editedMarkerStyles,
  editFieldStyles,
  groupedWrapperStyles,
  gutterTimestampStyles,
  headerStyles,
  highlightedStyles,
  mentionStyles,
  rootStyles,
  timestampStyles,
  wrapperStyles,
} from './TeamChatMessage.styles';
import { TeamChatMessageTestIds } from './TeamChatMessage.testIds';

const TeamChatMessage = memo(
  ({
    message,
    chatId = null,
    currentUserId = null,
    members = EMPTY_MEMBERS,
    isHighlighted = false,
    isPinned = false,
    isGrouped = false,
    replyParent = null,
    replyCount = 0,
    onReplyTo = null,
    onTogglePin = null,
  }) => {
    const dispatch = useDispatch();
    const sender = getMessageSender(message, members);
    const text = getMessageText(message);
    const timestamp = getMessageTimestamp(message);
    const updated = getMessageUpdated(message);
    const messageId = getMessageId(message);
    const reactions = getMessageReactions(message);
    const isFile = isFileMessage(message);
    const file = isFile ? getMessageFile(message) : null;

    const currentChatUserId = useMemo(
      () => resolveCurrentChatUserId(members, currentUserId),
      [members, currentUserId],
    );
    const isOwn = !!sender.userId && sender.userId === currentUserId;
    const isEditable = isOwn && !!chatId && !!messageId && !isFile;
    const canReact = !!chatId && !!messageId;

    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(text);
    const [isSaving, setIsSaving] = useState(false);
    const [isLinkCopied, setIsLinkCopied] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);

    const canCopyLink = !!chatId && !!messageId;
    const canPin = !!messageId && !!onTogglePin;
    const canReply = !!messageId && !!onReplyTo;
    const domId = getTeamChatMessageDomId(messageId);

    const onPinClick = useCallback(() => {
      if (!canPin) {
        return;
      }

      onTogglePin(messageId);
    }, [canPin, onTogglePin, messageId]);

    const onReplyClick = useCallback(() => {
      if (!canReply) {
        return;
      }

      onReplyTo(messageId);
    }, [canReply, onReplyTo, messageId]);

    const onCopyLink = useCallback(() => {
      const permalink = buildTeamChatMessagePermalink({ chatId, messageId });

      if (!permalink) {
        return;
      }

      copyToClipboard(permalink);
      setIsLinkCopied(true);
      window.setTimeout(() => setIsLinkCopied(false), LINK_COPIED_DURATION_MS);
    }, [chatId, messageId]);

    const subjectRef = useMemo(() => getMessageSubjectRef(message), [message]);
    const tokens = useMemo(() => renderMessageTextTokens(text, { members }), [text, members]);

    const senderMember = useMemo(() => {
      if (!sender.userId) {
        return { userId: sender.userId, nickname: sender.nickname };
      }

      const match = members.find(member => getMemberId(member) === sender.userId);

      return match ?? { userId: sender.userId, nickname: sender.nickname };
    }, [members, sender.userId, sender.nickname]);

    const containerStyle = useMemo(
      () => ({
        ...rootStyles,
        ...(isHighlighted ? highlightedStyles : null),
      }),
      [isHighlighted],
    );

    const wrapperStyle = isGrouped ? groupedWrapperStyles : wrapperStyles;

    const onEditStart = useCallback(() => {
      setDraft(text);
      setIsEditing(true);
    }, [text]);

    const onEditCancel = useCallback(() => {
      setIsEditing(false);
      setDraft(text);
    }, [text]);

    const onDraftChange = useCallback(e => {
      setDraft(e.target.value);
    }, []);

    const onReactionSelect = useCallback(
      emoji => {
        if (!canReact) {
          return;
        }

        dispatch(addReactionAction({ chatId, messageId, emoji }));
      },
      [dispatch, canReact, chatId, messageId],
    );

    const onReactionToggle = useCallback(
      (emoji, isOwnReaction) => {
        if (!canReact) {
          return;
        }

        if (isOwnReaction) {
          dispatch(removeReactionAction({ chatId, messageId, emoji }));

          return;
        }

        dispatch(addReactionAction({ chatId, messageId, emoji }));
      },
      [dispatch, canReact, chatId, messageId],
    );

    const onEditSave = useCallback(async () => {
      const trimmed = draft.trim();

      if (!trimmed || trimmed === text) {
        setIsEditing(false);

        return;
      }

      setIsSaving(true);

      try {
        await dispatch(updateMessageAction({ chatId, messageId, currentUserId, message: trimmed }));
        setIsEditing(false);
      } finally {
        setIsSaving(false);
      }
    }, [dispatch, draft, text, chatId, messageId, currentUserId]);

    const onMenuOpen = useCallback(e => {
      setMenuAnchor(e.currentTarget);
    }, []);

    const onMenuClose = useCallback(() => {
      setMenuAnchor(null);
    }, []);

    const menuItems = useMemo(() => {
      const items = [];

      if (canReply) {
        items.push({ text: 'Reply in thread', icon: <ReplyIcon />, onClick: onReplyClick });
      }

      if (canPin) {
        items.push({
          text: isPinned ? 'Unpin message' : 'Pin message',
          icon: isPinned ? <PinFilledIcon /> : <PinOutlinedIcon />,
          onClick: onPinClick,
        });
      }

      if (canCopyLink) {
        items.push({
          text: isLinkCopied ? 'Link copied' : 'Copy message link',
          icon: <DocLinkIcon />,
          onClick: onCopyLink,
        });
      }

      if (isEditable) {
        items.push({ text: 'Edit message', icon: <EditPenIcon />, onClick: onEditStart });
      }

      return items;
    }, [
      canReply,
      canPin,
      canCopyLink,
      isEditable,
      isPinned,
      isLinkCopied,
      onReplyClick,
      onPinClick,
      onCopyLink,
      onEditStart,
    ]);

    const hasMenuActions = menuItems.length > 0;
    const showActions = !isEditing && (canReact || hasMenuActions);
    const showHeader = !isGrouped;

    return (
      <Box className={MESSAGE_ROW_CLASS_NAME} id={domId} sx={wrapperStyle}>
        {showHeader ? (
          <Box sx={avatarColumnStyles}>
            <TeamChatMemberAvatar member={senderMember} showPresence={false} size={32} />
          </Box>
        ) : (
          <Box sx={gutterTimestampStyles}>
            {!!timestamp && (
              <Typography color="text.secondary" variant="regular12">
                {dayjs(timestamp).format(SHORT_TIME_FORMAT)}
              </Typography>
            )}
          </Box>
        )}
        <Box sx={containerStyle}>
          {showHeader && (
            <Box sx={headerStyles}>
              <Typography variant="bold14">{sender.nickname || UNKNOWN_SENDER_LABEL}</Typography>
              {!!timestamp && (
                <Typography sx={timestampStyles} variant="regular12">
                  {dayjs(timestamp).format(SHORT_DATETIME_FORMAT)}
                </Typography>
              )}
              {!_.isNil(updated) && (
                <Typography sx={editedMarkerStyles} variant="regular12">
                  (edited)
                </Typography>
              )}
            </Box>
          )}
          {showActions && (
            <Box sx={actionsStyles}>
              {canReact && <TeamChatReactionPicker onSelect={onReactionSelect} />}
              {hasMenuActions && (
                <Tooltip title="More actions">
                  <IconButton
                    aria-label="More actions"
                    data-testid={TeamChatMessageTestIds.MORE_ACTIONS_BUTTON}
                    size="small"
                    sx={actionButtonStyles}
                    onClick={onMenuOpen}
                  >
                    <MoreIcon size={IconSize[18]} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          {hasMenuActions && (
            <MenuPopoverCDS
              disablePortal
              anchorEl={menuAnchor}
              items={menuItems}
              onClose={onMenuClose}
            />
          )}
          {isEditing && (
            <Box>
              <TextFieldCDS
                autoFocus
                fullWidth
                multiline
                dataTestId={TeamChatMessageTestIds.EDIT_INPUT}
                disabled={isSaving}
                maxRows={4}
                name="teamChatMessageEdit"
                size="small"
                sx={editFieldStyles}
                value={draft}
                onChange={onDraftChange}
              />
              <Box sx={editActionsStyles}>
                <Button
                  data-testid={TeamChatMessageTestIds.EDIT_CANCEL_BUTTON}
                  disabled={isSaving}
                  size="small"
                  variant="outlined"
                  onClick={onEditCancel}
                >
                  Cancel
                </Button>
                <Button
                  data-testid={TeamChatMessageTestIds.EDIT_SAVE_BUTTON}
                  disabled={isSaving || !draft.trim()}
                  size="small"
                  variant="contained"
                  onClick={onEditSave}
                >
                  Save
                </Button>
              </Box>
            </Box>
          )}
          {!isEditing && !!replyParent && (
            <TeamChatReplyPreview members={members} parent={replyParent} />
          )}
          {!isEditing && isFile && !!file?.blobUrl && <TeamChatFileMessage file={file} />}
          {!isEditing && !isFile && !!subjectRef && (
            <TeamChatFieldAnchor anchor={subjectRef.path} label={subjectRef.path} />
          )}
          {!isEditing && !isFile && (
            <Typography variant="regular14">
              {tokens.map(token => {
                if (token.type === 'entityRef') {
                  return (
                    <TeamChatEntityChip
                      chatId={chatId}
                      entityId={token.entityId}
                      entityType={token.entityType}
                      key={token.key}
                      label={token.label}
                    />
                  );
                }

                if (token.type === 'mention') {
                  return (
                    <Typography
                      component="span"
                      key={token.key}
                      sx={mentionStyles}
                      variant="bold14"
                    >
                      {token.value}
                    </Typography>
                  );
                }

                return <span key={token.key}>{token.value}</span>;
              })}
            </Typography>
          )}
          <TeamChatReactions
            currentChatUserId={currentChatUserId}
            reactions={reactions}
            onToggle={canReact ? onReactionToggle : null}
          />
          {replyCount > 0 && (
            <Typography sx={timestampStyles} variant="regular12">
              {`${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}
            </Typography>
          )}
        </Box>
      </Box>
    );
  },
);
TeamChatMessage.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    message: PropTypes.string,
  }).isRequired,
  chatId: PropTypes.string,
  currentUserId: PropTypes.string,
  members: PropTypes.arrayOf(PropTypes.object),
  isHighlighted: PropTypes.bool,
  isPinned: PropTypes.bool,
  isGrouped: PropTypes.bool,
  replyParent: PropTypes.object,
  replyCount: PropTypes.number,
  onReplyTo: PropTypes.func,
  onTogglePin: PropTypes.func,
};

export default TeamChatMessage;
